<template>
  <article>
    <activity-stage :customRootContainerClass="'bits-about-it'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" id="bits-about-it-content">
        <aside>
          <figure
            v-for="step of currentRound.steps?.filter(step => !!step.optionalImage?.image?.src)"
            :aria-hidden="true"
            :class="`image id-${step.optionalImage?.image?.id}`"
            :key="step.optionalImage?.image?.id"
          >
            <img
              :alt="step.optionalImage?.image?.description"
              :src="step.optionalImage?.image?.src"
            />
          </figure>
        </aside>
        <div :class="{ 'text-display-lists-container': true, isMobile }">
          <text-display-list
            v-for="(steps, stepIndex) of currentRound.chunkedSteps"
            :key="stepIndex"
          >
            <li
              v-for="({ id, answer }, index) of steps"
              :class="[`step answer-${id}`]"
              :key="index"
            >
              <blinking-cursor-text
                :text="answer"
                :text-step="2 * stepIndex + index"
              ></blinking-cursor-text>
            </li>
          </text-display-list>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import _ from 'lodash';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger, AccessibilityHelper],
  data() {
    return {
      activityName: 'FillBlankSentences'
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ steps, ...round }) => {
        steps
          .filter(({ optionalImage }) => optionalImage)
          .forEach(({ optionalImage }) => {
            this.queueImage(optionalImage?.image?.src);
          });
      });

      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds: rounds.map(({ steps, ...rest }) => ({
          ...rest,
          chunkedSteps: _.chunk(steps, 2),
          steps
        }))
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        this.hideAllSteps();
        this.fadeInStep(this.currentStep.id);
        TweenMax.to('.bits-about-it', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();
      
      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.handleStepEnd();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.finishStep();
      this.fadeInStep(this.currentStep.id);
    },

    finishStep() {
      this.sndBell();
      if (this.currentStep.optionalImage) {
        const imageFigure = document.getElementsByClassName(
          `id-${this.currentStep?.optionalImage?.image?.id}`
        )?.[0];
        imageFigure?.setAttribute('aria-hidden', false);
        this.fadeInImage(this.currentStep?.optionalImage?.image?.id);
      }
      this.completeStep();
      this.setCurrentStep();
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      }
    },

    fadeInStep(answerTextId) {
      TweenMax.to(`.step.answer-${answerTextId}`, 0.5, { opacity: 1 });
    },

    fadeInImage(stepImageId) {
      TweenMax.to(`.id-${stepImageId}`, 0.5, { opacity: 1 });
    },

    hideAllSteps() {
      TweenMax.set('.step', { opacity: 0 });
      TweenMax.set('.image', { opacity: 0 });
    }
  },
  watch: {
    expectedKey(l) {
      const { stepIndex } = this.progress;
      const currentSteps = this.currentRound?.chunkedSteps?.flat();
      const answer = currentSteps?.[stepIndex]?.answer;
      const instructionPrefix = `Type the sentence -`;

      if (!answer || `${instructionPrefix} ${answer}` === this.currentScreenReaderAlert) return;

      this.setScreenReaderAlert(`${instructionPrefix} ${answer}`);
    }
  }
};
</script>

<style lang="scss" scoped>
.bits-about-it {
  opacity: 0;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: 22px;

    .title {
      font-weight: bold;
      margin-bottom: 10px;
    }

    .step {
      display: flex;
      flex: 1;
      justify-content: flex-start;

      &.padded {
        padding-top: 40px;
      }
    }
  }

  aside {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-around;
    min-width: 30%;

    figure {
      margin: 0 auto;

      img {
        height: auto;
        width: 100%;
      }
    }
  }

  .text-display-lists-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-around;

    &.isMobile {
      padding-right: 63px;
    }
  }
}

#bits-about-it-content {
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: center;
}
</style>
